import { defineChain } from "viem";

const ankrKey =
  typeof import.meta !== "undefined" && import.meta.env?.VITE_ANKR_KEY
    ? String(import.meta.env.VITE_ANKR_KEY).trim()
    : "";

/** Same pattern as trinity `src/config/chains.js` (Ankr multi-chain RPC). */
function ankr(chainPath, publicFallback) {
  return ankrKey
    ? `https://rpc.ankr.com/${chainPath}/${ankrKey}`
    : publicFallback;
}

/** EIP-55 checksum — required by ethers Contract / multicall targets. */
const MULTICALL3 = "0xcA11bde05977b3631167028862bE2a173976CA11";

/** RPC URLs shared by defineChain exports and the chains registry below. */
const rpc = {
  mainnet: ankr("eth", "https://rpc-ethereum.g4mm4.io"),
  optimism: ankr("optimism", "https://mainnet.optimism.io"),
  bsc: ankr("bsc", "https://bsc-dataseed.binance.org"),
  polygon: ankr("polygon", "https://polygon-rpc.com"),
  sonic: ankr("sonic_mainnet", "https://rpc.soniclabs.com"),
  pulsechain: "https://rpc.pulsechain.com",
  hyperliquid: "https://rpc.hyperliquid.xyz/evm",
  moonbeam: "https://rpc.api.moonbeam.network", // not on Ankr
  robinhood: "https://rpc.mainnet.chain.robinhood.com",
  mantle: ankr("mantle", "https://rpc.mantle.xyz"),
  base: ankr("base", "https://mainnet.base.org"),
  mode: "https://mainnet.mode.network",
  arbitrum: ankr("arbitrum", "https://arb1.arbitrum.io/rpc"),
  hemi: "https://rpc.hemi.network/rpc",
  avalanche: ankr("avalanche", "https://api.avax.network/ext/bc/C/rpc"),
  linea: ankr("linea", "https://rpc.linea.build"),
  berachain: "https://rpc.berachain.com",
  blast: ankr("blast", "https://rpc.blast.io"),
  scroll: ankr("scroll", "https://rpc.scroll.io"),
};

export const mainnet = defineChain({
  id: 1,
  name: "Ethereum",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  blockTime: 12_000,
  rpcUrls: {
    default: { http: [rpc.mainnet] },
  },
  blockExplorers: {
    default: {
      name: "Etherscan",
      url: "https://etherscan.io",
      apiUrl: "https://api.etherscan.io/api",
    },
  },
  contracts: {
    ensUniversalResolver: {
      address: "0xeeeeeeee14d718c2b47d9923deab1335e144eeee",
      blockCreated: 23_085_558,
    },
    multicall3: {
      address: MULTICALL3,
      blockCreated: 14_353_601,
    },
  },
});

export const optimism = defineChain({
  id: 10,
  name: "Optimism",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  sourceId: 1,
  rpcUrls: {
    default: { http: [rpc.optimism] },
  },
  blockExplorers: {
    default: {
      name: "Optimism Explorer",
      url: "https://optimistic.etherscan.io",
      apiUrl: "https://api-optimistic.etherscan.io/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 4_286_263,
    },
  },
});

export const bsc = defineChain({
  id: 56,
  name: "BNB Smart Chain",
  blockTime: 750,
  nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.bsc] },
  },
  blockExplorers: {
    default: {
      name: "BscScan",
      url: "https://bscscan.com",
      apiUrl: "https://api.bscscan.com/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 15_921_452,
    },
  },
});

export const polygon = defineChain({
  id: 137,
  name: "Polygon",
  blockTime: 2_000,
  nativeCurrency: { name: "Polygon", symbol: "MATIC", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.polygon] },
  },
  blockExplorers: {
    default: {
      name: "PolygonScan",
      url: "https://polygonscan.com",
      apiUrl: "https://api.polygonscan.com/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 25_770_160,
    },
  },
});

export const sonic = defineChain({
  id: 146,
  name: "Sonic Mainnet",
  blockTime: 630,
  nativeCurrency: { name: "Sonic", symbol: "S", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.sonic] },
  },
  blockExplorers: {
    default: {
      name: "Sonic Explorer",
      url: "https://sonicscan.org",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 60,
    },
  },
  testnet: false,
});

export const pulsechain = defineChain({
  id: 369,
  name: "PulseChain",
  nativeCurrency: { name: "Pulse", symbol: "PLS", decimals: 18 },
  testnet: false,
  blockTime: 10_000,
  rpcUrls: {
    default: {
      http: [rpc.pulsechain],
      // http: ["https://rpc-pulsechain.g4mm4.io"],
      // http: ["https://rpc.libertyswap.finance"],
      webSocket: ["wss://ws.pulsechain.com"],
    },
  },
  blockExplorers: {
    default: {
      name: "PulseScan",
      url: "https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/",
      apiUrl: "https://api.scan.pulsechain.com/api",
    },
  },
  contracts: {
    ensRegistry: {
      address: "0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e",
    },
    multicall3: {
      address: MULTICALL3,
      blockCreated: 14_353_601,
    },
  },
});

export const hyperliquid = defineChain({
  id: 999,
  name: "HyperEVM",
  nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.hyperliquid] },
  },
  blockExplorers: {
    default: {
      name: "HyperEVMScan",
      url: "https://hyperevmscan.io",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 13_051,
    },
  },
  testnet: false,
});

export const moonbeam = defineChain({
  id: 1284,
  name: "Moonbeam",
  nativeCurrency: { name: "Moonbeam", symbol: "GLMR", decimals: 18 },
  rpcUrls: {
    default: {
      http: [rpc.moonbeam],
      webSocket: ["wss://wss.api.moonbeam.network"],
    },
  },
  blockExplorers: {
    default: {
      name: "Moonscan",
      url: "https://moonscan.io",
      apiUrl: "https://api-moonbeam.moonscan.io/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 609_002,
    },
  },
  testnet: false,
});

export const robinhood = defineChain({
  id: 4663,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  blockTime: 100,
  rpcUrls: {
    default: { http: [rpc.robinhood] },
  },
  blockExplorers: {
    default: {
      name: "Blockscout",
      url: "https://robinhoodchain.blockscout.com",
      apiUrl: "https://robinhoodchain.blockscout.com/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
    },
  },
});

export const mantle = defineChain({
  id: 5000,
  name: "Mantle",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.mantle] },
  },
  blockExplorers: {
    default: {
      name: "Mantle Explorer",
      url: "https://explorer.mantle.xyz",
      apiUrl: "https://api.mantlescan.xyz/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 304_717,
    },
  },
});

export const base = defineChain({
  id: 8453,
  name: "Base",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  sourceId: 1,
  rpcUrls: {
    default: { http: [rpc.base] },
  },
  blockExplorers: {
    default: {
      name: "Basescan",
      url: "https://basescan.org",
      apiUrl: "https://api.basescan.org/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 5_022,
    },
  },
});

export const mode = defineChain({
  id: 34443,
  name: "Mode",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  sourceId: 1,
  rpcUrls: {
    default: { http: [rpc.mode] },
  },
  blockExplorers: {
    default: {
      name: "Mode Explorer",
      url: "https://explorer.mode.network",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 2_465_882,
    },
  },
});

export const arbitrum = defineChain({
  id: 42_161,
  name: "Arbitrum One",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  blockTime: 250,
  rpcUrls: {
    default: { http: [rpc.arbitrum] },
  },
  blockExplorers: {
    default: {
      name: "Arbiscan",
      url: "https://arbiscan.io",
      apiUrl: "https://api.arbiscan.io/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 7_654_707,
    },
  },
});

export const hemi = defineChain({
  id: 43_111,
  name: "Hemi",
  network: "Hemi",
  blockTime: 12_000,
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.hemi] },
  },
  blockExplorers: {
    default: {
      name: "blockscout",
      url: "https://explorer.hemi.xyz",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
    },
  },
  testnet: false,
});

export const avalanche = defineChain({
  id: 43_114,
  name: "Avalanche",
  blockTime: 1_700,
  nativeCurrency: { name: "Avalanche", symbol: "AVAX", decimals: 18 },
  rpcUrls: {
    default: { http: [rpc.avalanche] },
  },
  blockExplorers: {
    default: {
      name: "SnowTrace",
      url: "https://snowtrace.io",
      apiUrl: "https://api.snowtrace.io",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 11_907_934,
    },
  },
});

export const linea = defineChain({
  id: 59_144,
  name: "Linea",
  blockTime: 2_000,
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: {
      http: [rpc.linea],
      webSocket: ["wss://rpc.linea.build"],
    },
  },
  blockExplorers: {
    default: {
      name: "Lineascan",
      url: "https://lineascan.build",
      apiUrl: "https://api.lineascan.build/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 42,
    },
  },
  testnet: false,
});

export const berachain = defineChain({
  id: 80_094,
  name: "Berachain",
  blockTime: 2_000,
  nativeCurrency: {
    decimals: 18,
    name: "BERA Token",
    symbol: "BERA",
  },
  rpcUrls: {
    default: { http: [rpc.berachain] },
  },
  blockExplorers: {
    default: {
      name: "Berascan",
      url: "https://berascan.com",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 0,
    },
  },
  testnet: false,
});

export const blast = defineChain({
  id: 81_457,
  name: "Blast",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  sourceId: 1,
  rpcUrls: {
    default: { http: [rpc.blast] },
  },
  blockExplorers: {
    default: {
      name: "Blastscan",
      url: "https://blastscan.io",
      apiUrl: "https://api.blastscan.io/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 212_929,
    },
  },
});

export const scroll = defineChain({
  id: 534_352,
  name: "Scroll",
  blockTime: 3_000,
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: {
      http: [rpc.scroll],
      webSocket: ["wss://wss-rpc.scroll.io/ws"],
    },
  },
  blockExplorers: {
    default: {
      name: "Scrollscan",
      url: "https://scrollscan.com",
      apiUrl: "https://api.scrollscan.com/api",
    },
  },
  contracts: {
    multicall3: {
      address: MULTICALL3,
      blockCreated: 14,
    },
  },
  testnet: false,
});

/** Product-supported EVM chains: ETH, BSC, Robinhood, Base, Arbitrum. Solana is wired separately. */
export const wagmiChains = [
  mainnet,
  bsc,
  robinhood,
  base,
  arbitrum,
];

export const chains = {
  1: {
    dbankId: "eth",
    hexChainId: "0x1",
    name: "Ethereum",
    network: "mainnet",
    currency: "ETH",
    explorerUrl: "https://etherscan.io/",
    rpcUrl: rpc.mainnet,
    multicallAddress: MULTICALL3,
  },
  10: {
    dbankId: "op",
    hexChainId: "0xa",
    name: "Optimism",
    network: "optimism",
    currency: "ETH",
    explorerUrl: "https://optimistic.etherscan.io/",
    rpcUrl: rpc.optimism,
  },
  56: {
    dbankId: "bsc",
    hexChainId: "0x38",
    name: "BNB Smart Chain",
    network: "bsc",
    currency: "AVAX",
    explorerUrl: "https://bscscan.com/",
    rpcUrl: rpc.bsc,
  },
  137: {
    dbankId: "matic",
    hexChainId: "0x89",
    name: "Polygon",
    network: "polygon",
    currency: "MATIC",
    explorerUrl: "https://polygonscan.com/",
    rpcUrl: rpc.polygon,
  },
  146: {
    dbankId: "sonic",
    hexChainId: "0x92",
    name: "Sonic Mainnet",
    network: "sonic",
    currency: "S",
    explorerUrl: "https://sonicscan.org/",
    rpcUrl: rpc.sonic,
  },
  369: {
    dbankId: "pls",
    hexChainId: "0x171",
    name: "PulseChain",
    network: "pulsechain",
    currency: "PLS",
    explorerUrl:
      "https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/",
    rpcUrl: rpc.pulsechain,
    wssUrl: "wss://rpc.pulsechain.com",
    multicallAddress: MULTICALL3,
  },
  999: {
    dbankId: "hyper",
    name: "HyperEVM",
    network: "HyperEVM",
    currency: "HYPE",
    explorerUrl: "https://hyperevmscan.io/",
    rpcUrl: rpc.hyperliquid,
  },
  // 1284 Moonbeam — parked (defineChain + rpc.moonbeam kept above)
  4663: {
    dbankId: "robinhood",
    hexChainId: "0x1237",
    name: "Robinhood Chain",
    network: "robinhood",
    currency: "ETH",
    explorerUrl: "https://robinhoodchain.blockscout.com/",
    rpcUrl: rpc.robinhood,
    multicallAddress: MULTICALL3,
  },
  5000: {
    dbankId: "mantle",
    hexChainId: "0x1388",
    name: "Mantle",
    network: "mantle",
    currency: "ETH",
    explorerUrl: "https://explorer.mantle.xyz/",
    rpcUrl: rpc.mantle,
  },
  8453: {
    dbankId: "base",
    hexChainId: "0x2105",
    name: "Base",
    network: "base",
    currency: "ETH",
    explorerUrl: "https://basescan.org/",
    rpcUrl: rpc.base,
  },
  34443: {
    dbankId: "mode",
    hexChainId: "0x868b",
    name: "Mode",
    network: "mode",
    currency: "ETH",
    explorerUrl: "https://explorer.mode.network/",
    rpcUrl: rpc.mode,
  },
  42161: {
    dbankId: "arb",
    hexChainId: "0xa4b1",
    name: "Arbitrum One",
    network: "arbitrum",
    currency: "ETH",
    explorerUrl: "https://arbiscan.io/",
    rpcUrl: rpc.arbitrum,
  },
  43111: {
    dbankId: "hemi",
    hexChainId: "0xa867",
    name: "Hemi",
    network: "hemi",
    currency: "ETH",
    explorerUrl: "https://explorer.hemi.xyz/",
    rpcUrl: rpc.hemi,
  },
  43114: {
    dbankId: "avax",
    hexChainId: "0xa86a",
    name: "Avalanche",
    network: "avalanche",
    currency: "AVAX",
    explorerUrl: "https://snowtrace.io/",
    rpcUrl: rpc.avalanche,
  },
  59144: {
    dbankId: "linea",
    hexChainId: "0xe708",
    name: "Linea",
    network: "linea",
    currency: "ETH",
    explorerUrl: "https://lineascan.build/",
    rpcUrl: rpc.linea,
  },
  80094: {
    dbankId: "bera",
    hexChainId: "0x138de",
    name: "Berachain",
    network: "bera",
    currency: "BERA",
    explorerUrl: "https://berascan.com/",
    rpcUrl: rpc.berachain,
  },
  81457: {
    dbankId: "blast",
    hexChainId: "0x13e31",
    name: "Blast",
    network: "blast",
    currency: "ETH",
    explorerUrl: "https://blastscan.io/",
    rpcUrl: rpc.blast,
  },
  534352: {
    dbankId: "scroll",
    hexChainId: "0x82750",
    name: "Scroll",
    network: "scroll",
    currency: "ETH",
    explorerUrl: "https://scrollscan.com/",
    rpcUrl: rpc.scroll,
  },
};
